import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Archive, CheckCircle2, TriangleAlert } from 'lucide-react';
import { type JSX, useEffect, useState } from 'react';
import { Button, Checkbox, DatePicker, Dialog, Input, Radio } from '~/components/primitives';
import { ApiRequestError } from '~/lib/api/client';
import { fetchProject } from '~/lib/api/projects';
import { formatDate } from '~/lib/format';
import {
  type OpResult,
  duplicateProject,
  fetchDuplicatePreview,
  shiftIsoDate,
} from '~/lib/tasks/api/blueprints';
import { businessTodayIso } from '~/lib/tasks/business-day';
import { AnchorPreview } from './AnchorPreview';
import { FooterReason } from './FooterReason';
import { TemplateOpSummary } from './TemplateOpSummary';
import styles from './dialogs.module.css';

/** Целое число дней между двумя ISO-датами (для span duplicate-shift превью). */
function isoDaysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/**
 * F2.5 — «Дублировать проект». Второй столп решения (перезапуск похожего
 * заказа). Default statuses='reset' — перевёрнут скептиком осознанно.
 */
export type DuplicateProjectDialogProps = {
  project: { id: string; name: string };
  onClose: () => void;
};

type StatusMode = 'reset' | 'keep' | 'incomplete_only';

export function DuplicateProjectDialog({
  project,
  onClose,
}: DuplicateProjectDialogProps): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState(m.tmpl_copy_suffix({ name: project.name }));
  /*
   * F16 — дефолт «сдвинуть к дате», а не «как есть».
   *
   * `as_is` был главным живым источником проектов в прошлом: копия молча
   * наследовала даты источника, и перезапуск похожего заказа рождал план с
   * просроченными задачами. «Как есть» остаётся, но теперь это осознанный
   * выбор — со своим превью диапазона.
   */
  const [dates, setDates] = useState<'shift' | 'as_is'>('shift');
  const [anchorDate, setAnchorDate] = useState<string | null>(() => businessTodayIso());
  const [statuses, setStatuses] = useState<StatusMode>('reset');
  const [copyAssignees, setCopyAssignees] = useState(true);
  const [copyCustomer, setCopyCustomer] = useState(true);
  const [copyMembers, setCopyMembers] = useState(false);
  const [reattachSecondary, setReattachSecondary] = useState(false);
  // project-visibility (F12) — видимость копии: по умолчанию наследуется от источника.
  const [copyVisibility, setCopyVisibility] = useState<'open' | 'private' | null>(null);
  const [result, setResult] = useState<OpResult | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // F4.3 — span проекта-источника для превью при dates=shift (снапшота клиентски
  // нет, берём плановый диапазон проекта: последняя дата = anchor + span).
  const detail = useQuery({
    queryKey: ['project', project.id],
    queryFn: () => fetchProject(project.id),
  });
  const startDate = typeof detail.data?.start_date === 'string' ? detail.data.start_date : null;
  const endDate = typeof detail.data?.end_date === 'string' ? detail.data.end_date : null;
  const span = startDate && endDate ? isoDaysBetween(startDate, endDate) : 0;
  const hasDates = Boolean(startDate && endDate);
  /**
   * Даты источника приезжают запросом, а дефолт режима — `shift` (решение F16).
   * Поэтому мало выключить кнопку: к моменту ответа выбранным уже стоит режим,
   * которого у этого проекта быть не может. Возвращаем в «как есть» ровно один
   * раз, когда стало известно, что дат нет.
   */
  useEffect(() => {
    if (detail.data && !hasDates && dates === 'shift') setDates('as_is');
  }, [detail.data, hasDates, dates]);

  // project-visibility (F12) — эффективная видимость копии: выбор пользователя либо
  // наследование источника. Предупреждаем при рассекречивании private→open.
  const sourceVisibility: 'open' | 'private' =
    detail.data?.visibility === 'private' ? 'private' : 'open';
  const effectiveVisibility = copyVisibility ?? sourceVisibility;
  const visibilityDowngrade = sourceVisibility === 'private' && effectiveVisibility === 'open';

  // F64 (TPL-F9) — число чужих проектов считает сервер (B25): у клиента его нет.
  const preview = useQuery({
    queryKey: ['task-module', 'projects', project.id, 'duplicate-preview'],
    queryFn: () => fetchDuplicatePreview(project.id),
  });
  const affectedProjects = preview.data?.affected_projects_count ?? 0;

  const dup = useMutation({
    mutationFn: () =>
      duplicateProject(project.id, {
        name: name.trim(),
        dates,
        ...(dates === 'shift' && anchorDate ? { anchor_date: anchorDate } : {}),
        statuses,
        copy_assignees: copyAssignees,
        copy_customer: copyCustomer,
        copy_members: copyMembers,
        reattach_secondary: reattachSecondary,
        visibility: effectiveVisibility,
      }),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['projects-metrics'] });
      qc.invalidateQueries({ queryKey: ['task-module'] });
    },
    onError: (e) => setErrorCode(e instanceof ApiRequestError ? e.code : 'server.internal'),
  });

  /*
   * F-D3 — план получившейся копии: в режиме сдвига считаем от якоря на длину
   * исходного диапазона, в «как есть» это даты источника. Без дат у источника
   * строки нет вовсе — врать про план, которого не будет, нельзя.
   */
  const copyFirst = dates === 'shift' ? anchorDate : startDate;
  const copyLast =
    dates === 'shift' ? (anchorDate && hasDates ? shiftIsoDate(anchorDate, span) : null) : endDate;

  const statusOptions: Array<{ value: StatusMode; label: string; hint: string }> = [
    { value: 'reset', label: m.tmpl_status_reset(), hint: m.tmpl_status_reset_hint() },
    { value: 'keep', label: m.tmpl_status_keep(), hint: m.tmpl_status_keep_hint() },
    {
      value: 'incomplete_only',
      label: m.tmpl_status_incomplete(),
      hint: m.tmpl_status_incomplete_hint(),
    },
  ];

  /** F100 круг 4: почему «Дублировать» выключена — названо в подвале. */
  const dupBlockedReason = !name.trim()
    ? m.tmpl_reason_name_required()
    : dates === 'shift' && !anchorDate
      ? m.tmpl_reason_anchor_required()
      : null;
  const dupBlockedFor = !name.trim() ? 'duplicate-name' : 'duplicate-anchor-date';

  /**
   * F100 (С-I; К5-20): живая область последствий живёт в подвале ВСЕГДА — и на шаге формы, и
   * на шаге результата: часть скринридеров не объявляет регион, созданный уже с текстом.
   */
  const reattachLive = (
    <p
      id="duplicate-reattach-consequence"
      className={styles.consequence}
      data-testid="duplicate-reattach-consequence"
      role="status"
    >
      {reattachSecondary && affectedProjects > 0 && (
        <>
          {/* Круг 1 UI: предупреждение — значком и цветом, не одним цветом (§1.3). */}
          <TriangleAlert size={14} aria-hidden="true" />
          <span>{m.tmpl_reattach_consequence({ n: affectedProjects })}</span>
        </>
      )}
    </p>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={result ? m.tmpl_duplicate_done_title() : m.tmpl_duplicate_title()}
      size="md"
      /*
       * F100 (С-I, NC-61; § 10.6 п. 4): подвал — слот `footer` `Dialog` на КАЖДОМ шаге, а не
       * липкий блок внутри прокручиваемого тела: полоса прокрутки тела заходила в подвал,
       * разделитель обрывался на её ширине.
       */
      footer={
        result ? (
          <>
            {reattachLive}
            {/* F60 (TPL-F1): откат только что созданной копии — существующей
                архивацией (Tier 2, подписант — не автор; B11), поэтому ведём в раздел
                архивации копии, а не удаляем молча отсюда. */}
            <Button
              variant="ghost"
              className={styles.footerStart}
              title={m.tmpl_duplicate_archive_copy_hint()}
              leadingIcon={<Archive size={14} />}
              onClick={() =>
                navigate({
                  to: '/work-tasks/$projectId',
                  params: { projectId: result.project_id },
                  search: { vt: 'settings', section: 'archive' },
                })
              }
            >
              {m.tmpl_duplicate_archive_copy()}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {m.tm_close()}
            </Button>
            <Button
              onClick={() =>
                navigate({ to: '/work-tasks/$projectId', params: { projectId: result.project_id } })
              }
            >
              {m.tmpl_go_to_project()}
            </Button>
          </>
        ) : (
          <>
            {/* F64 (TPL-F9), дословно UX-гейта F-D11 раунд 5: «для опции, меняющей чужие
              проекты, нужна строка последствий рядом с „Дублировать“ … она работает
              независимо от позиции прокрутки». Только когда опция включена и есть что менять. */}
            {/* Круг 2 UI: live-регион живёт в подвале всегда — часть скринридеров не
              объявляет регион, созданный уже с текстом; меняется только содержимое. */}
            {reattachLive}
            {/* F61 (TPL-F3, понижена до MINOR: видимость копии правится после создания —
              раздел «Видимость» настроек проекта). Раскрытие структуры всей компании —
              не справка рядом с «нет плановых дат», а последствие у самой кнопки. */}
            {visibilityDowngrade && (
              <p className={styles.consequence} data-testid="duplicate-visibility-consequence">
                <TriangleAlert size={14} aria-hidden="true" />
                <span>{m.tmpl_visibility_open_consequence()}</span>
              </p>
            )}
            {/* F100 круг 4 (UI, MAJOR): причина выключенного «Дублировать» — та же раскладка,
                что у остального семейства: слева, перед парой «Отмена» + действие. */}
            {dupBlockedReason && (
              <FooterReason id="duplicate-reason" htmlFor={dupBlockedFor}>
                {dupBlockedReason}
              </FooterReason>
            )}
            <Button variant="ghost" onClick={onClose}>
              {m.tm_cancel()}
            </Button>
            <Button
              loading={dup.isPending}
              disabled={dupBlockedReason !== null}
              focusableWhenDisabled
              // F100 круг 7 (UX): нажатие на выключенное действие ведёт в незаполненное поле.
              onDisabledClick={() => document.getElementById(dupBlockedFor)?.focus()}
              onClick={() => dup.mutate()}
              aria-describedby={dupBlockedReason ? 'duplicate-reason' : undefined}
            >
              {m.tmpl_action_duplicate()}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className={styles.body}>
          {/* F-D3 — дефект тот же, что в материализации: пустое тело и «Отмена»
              после уже созданной копии. Чиним обе двери, а не одну. */}
          <div className={styles.successBox} role="status">
            {/* F-D13 — глагол «создана» стоит в заголовке диалога; здесь
                называется предмет, а не повторяется факт. */}
            {/* F66 круг 1 (UI, MAJOR ×2): статус — значком и текстом, не одним цветом;
                в плашке — глагол и ссылка на саму копию, а не повтор «копия «… (копия)»». */}
            <span className={styles.successTitle}>
              <CheckCircle2 size={16} aria-hidden="true" className={styles.successIcon} />
              <span>
                {m.tmpl_duplicate_done_created()}{' '}
                <Link
                  className={styles.successLink}
                  to="/work-tasks/$projectId"
                  params={{ projectId: result.project_id }}
                >
                  {name}
                </Link>
              </span>
            </span>
            {copyFirst && copyLast && (
              <span className={styles.successPlan}>
                {m.tmpl_materialize_done_plan({
                  first: formatDate(copyFirst),
                  last: formatDate(copyLast),
                  tasks: result.task_count,
                })}
              </span>
            )}
          </div>
          <TemplateOpSummary
            skipped={result.skipped}
            droppedAssignees={result.dropped_assignees}
            showNothingSkipped
            included={[
              { key: 'tasks', label: m.tmpl_summary_included_tasks(), value: result.task_count },
              {
                key: 'deps',
                label: m.tmpl_summary_included_deps(),
                value: result.dependency_count,
              },
            ]}
          />
        </div>
      ) : (
        <div className={styles.body}>
          {/* F61 круг 1 (UI, MAJOR): фокус при открытии стоял на крестике — на первое поле. */}
          <Input
            id="duplicate-name"
            required
            label={m.tmpl_field_name()}
            value={name}
            data-autofocus="true"
            onChange={(e) => setName(e.target.value)}
          />

          {/* F61 круг 1 (UX, MINOR): без плановых дат у источника группа «Даты копии»
              сворачивается в одну строку — выключенный переключатель и выноска
              «переносить нечего» заставляли читать и листать ради отсутствующего выбора. */}
          {!hasDates ? (
            <p className={styles.datesNone}>{m.tmpl_dup_dates_none()}</p>
          ) : (
            <>
              {/* Раунд 2 гейта (UI, MINOR): у группы режимов дат не было подписи, а у
              соседней «Видимость копии» она есть. Подпись видимая — тем же
              классом, что и остальные подписи групп диалога. */}
              <div className={styles.group}>
                <span className={styles.fieldLabel} id="dup-dates-label">
                  {m.tmpl_dup_dates_group_label()}
                </span>
                <div
                  className={styles.segmented}
                  role="radiogroup"
                  aria-labelledby="dup-dates-label"
                >
                  <button
                    type="button"
                    role="radio"
                    data-label={m.tmpl_dates_as_is()}
                    aria-checked={dates === 'as_is'}
                    className={dates === 'as_is' ? styles.segActive : styles.seg}
                    onClick={() => setDates('as_is')}
                  >
                    {m.tmpl_dates_as_is()}
                  </button>
                  {/*
                   * Раунд 1 гейта F-D11 (UX, 🔴 BLOCKER, дословно): «Контрол обещает
                   * то, чего не будет — режим „Сдвинуть даты“ выбран, поле „Дата
                   * старта“ активно и заполнено 29.08.2026, а подпись ниже говорит
                   * „копия создастся без сроков“».
                   *
                   * У проекта без плановых дат сдвигать нечего: режим выключается и
                   * объясняет причину, а не принимает выбор, который ни на что не
                   * повлияет. Подпись остаётся в ветке «как есть» — там она отвечает
                   * на свой вопрос («что унаследует копия»), а не спорит с контролом.
                   */}
                  <button
                    type="button"
                    role="radio"
                    data-label={m.tmpl_dates_shift()}
                    aria-checked={dates === 'shift'}
                    disabled={!hasDates}
                    aria-disabled={!hasDates || undefined}
                    title={hasDates ? undefined : m.tmpl_dup_shift_no_dates_hint()}
                    className={dates === 'shift' ? styles.segActive : styles.seg}
                    onClick={() => setDates('shift')}
                  >
                    {m.tmpl_dates_shift()}
                  </button>
                </div>
              </div>
              {dates === 'shift' && hasDates ? (
                <>
                  <DatePicker
                    id="duplicate-anchor-date"
                    // F100 круг 6 (UX): дата обязательна в режиме сдвига — видно у подписи, а не
                    // только по выключенной кнопке в подвале.
                    required
                    label={m.tmpl_anchor_start_label()}
                    value={anchorDate}
                    onChange={setAnchorDate}
                  />
                  <AnchorPreview
                    hasDates={hasDates}
                    maxOffsetDays={span}
                    date={anchorDate}
                    dateMode="start_on"
                    dateAnchor
                  />
                </>
              ) : (
                /*
                 * F16 — «как есть» перестаёт быть слепой веткой. Раньше превью
                 * рендерилось только для сдвига, и пользователь выбирал наследование
                 * дат, не видя, КАКИЕ это даты и что они уже прошли.
                 */
                <div className={styles.anchorPreview}>
                  {hasDates && startDate && endDate ? (
                    <>
                      <div className={styles.anchorLine}>
                        {m.tmpl_dup_as_is_range({
                          first: formatDate(startDate),
                          last: formatDate(endDate),
                        })}
                      </div>
                      {endDate < businessTodayIso() && (
                        <div className={styles.anchorWarn}>
                          <TriangleAlert size={13} aria-hidden="true" />
                          <span>{m.tmpl_dup_as_is_past()}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.anchorLine}>{m.tmpl_dup_as_is_no_dates()}</div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Раунд 3 гейта (UI, MAJOR): подписана была ОДНА группа из четырёх, и
              её подпись читалась заголовком всего, что ниже. Подпись получает
              каждая группа — тем же классом, иначе граница групп не рисуется
              ничем, кроме одинакового вертикального ритма. */}
          <div className={styles.group}>
            <span className={styles.fieldLabel} id="dup-statuses-label">
              {m.tmpl_dup_statuses_group_label()}
            </span>
            <div
              className={styles.radioGroup}
              role="radiogroup"
              aria-labelledby="dup-statuses-label"
            >
              {/* F103 (С-I, TPL-F14): примитив `Radio` — та же отрисовка, что была здесь своей. */}
              {statusOptions.map((o) => (
                <Radio
                  key={o.value}
                  name="dup-statuses"
                  checked={statuses === o.value}
                  onChange={() => setStatuses(o.value)}
                  label={o.label}
                  description={o.hint}
                />
              ))}
            </div>
          </div>

          {/* project-visibility (F12) — видимость копии: наследуется, но можно сменить. */}
          <div className={styles.group}>
            <div className={styles.fieldLabel} id="dup-visibility-label">
              {m.tmpl_visibility_copy_label()}
            </div>
            <div
              className={styles.segmented}
              role="radiogroup"
              aria-labelledby="dup-visibility-label"
            >
              <button
                type="button"
                role="radio"
                data-label={m.project_visibility_open()}
                aria-checked={effectiveVisibility === 'open'}
                className={effectiveVisibility === 'open' ? styles.segActive : styles.seg}
                onClick={() => setCopyVisibility('open')}
              >
                {m.project_visibility_open()}
              </button>
              <button
                type="button"
                role="radio"
                data-label={m.project_visibility_private()}
                aria-checked={effectiveVisibility === 'private'}
                className={effectiveVisibility === 'private' ? styles.segActive : styles.seg}
                onClick={() => setCopyVisibility('private')}
              >
                {m.project_visibility_private()}
              </button>
            </div>
            {/* F61 (TPL-F3): раскрытие — одной строкой последствий у кнопки «Дублировать»
                (подвал в поле зрения всегда); здесь второй копией оно было бы шумом. */}
          </div>

          {/* F61 (TPL-F10): «Что скопировать» не описывало первую опцию — перепривязка
              ничего не копирует, она меняет задачи в ЧУЖИХ проектах. Своя подгруппа
              «Связи с другими проектами» — выше копий (порядок риска, раунд 4 F-D11). */}
          <div className={styles.group}>
            <span className={styles.fieldLabel} id="dup-links-label">
              {m.tmpl_dup_links_group_label()}
            </span>
            <div className={styles.checks} role="group" aria-labelledby="dup-links-label">
              {/*
               * Раунд 4 гейта F-D11 (UX, 🔴 BLOCKER, дословно): «Пользователь
               * физически может запустить копирование, ни разу не увидев
               * „перепривязать связанные задачи из других проектов“ — единственную
               * опцию с последствиями за пределами копии. Отсрочка по порядку
               * превратилась в сокрытие».
               *
               * Опция с последствиями ЗА ПРЕДЕЛАМИ копии (запись в чужие проекты)
               * идёт первой. Ту же инверсию иерархии риска UX-половина называла ещё
               * на раунде 1; тогда её отложили строкой книги, но подписи групп
               * подняли высоту формы, и отложенное стало невидимым. Порядок теперь
               * читается от последствий к удобству, а не наоборот.
               */}
              <Checkbox
                label={m.tmpl_opt_reattach_secondary()}
                description={m.tmpl_reattach_hint()}
                checked={reattachSecondary}
                {...(reattachSecondary && affectedProjects > 0
                  ? { 'aria-describedby': 'duplicate-reattach-consequence' }
                  : {})}
                onChange={(e) => setReattachSecondary(e.target.checked)}
              />
            </div>
          </div>
          <div className={styles.group}>
            <span className={styles.fieldLabel} id="dup-copy-label">
              {m.tmpl_dup_copy_group_label()}
            </span>
            <div className={styles.checks} role="group" aria-labelledby="dup-copy-label">
              <Checkbox
                label={m.tmpl_opt_assignees()}
                checked={copyAssignees}
                onChange={(e) => setCopyAssignees(e.target.checked)}
              />
              <Checkbox
                label={m.tmpl_opt_copy_customer()}
                checked={copyCustomer}
                onChange={(e) => setCopyCustomer(e.target.checked)}
              />
              <Checkbox
                label={m.tmpl_opt_copy_members()}
                checked={copyMembers}
                onChange={(e) => setCopyMembers(e.target.checked)}
              />
            </div>
          </div>

          {errorCode && <div className={styles.error}>{m.error_by_code(errorCode)}</div>}
        </div>
      )}
    </Dialog>
  );
}
